import React from 'react';
import { twMerge } from 'tailwind-merge';
import Footer from '../misc/Footer';
import NavigationBar from '../misc/NavigationBar';

interface PageWrapperProps {
  hideSidePanel?: boolean;
  children?: React.ReactNode;
  handleLogin?: () => void;
  stepIndicator?: React.ReactNode;
}

function ResponsePageWrapper({
  children,
  handleLogin,
  stepIndicator,
  hideSidePanel = false,
}: PageWrapperProps): JSX.Element {
  const header = React.Children.toArray(children).find((child) => React.isValidElement(child) && child.type === Header);
  const sidePanel = React.Children.toArray(children).find(
    (child) => React.isValidElement(child) && child.type === SidePanel,
  );

  return (
    <div className="flex min-h-screen flex-col">
      <NavigationBar handleLogin={handleLogin} />
      <div className="mt-2 flex w-full flex-1 flex-col items-center justify-center rounded-lg px-4 md:px-0">
        <div
          className={twMerge(
            'my-2 w-full rounded-lg bg-secondary md:my-4',
            hideSidePanel ? 'md:w-[500px]' : 'md:w-2/3 md:min-w-[750px]',
          )}>
          <div className="flex w-full flex-col p-2 md:flex-row md:p-6">
            {sidePanel ? (
              <div className="top-0 flex h-fit w-full justify-center overflow-y-auto md:sticky md:min-h-0 md:w-1/2">
                {React.isValidElement(sidePanel) ? sidePanel.props.children : null}
              </div>
            ) : hideSidePanel ? null : (
              <div className="hidden w-full justify-center md:flex md:min-h-0 md:w-1/2">
                <div className="flex h-full w-full flex-col items-center justify-center p-4">
                  <img
                    src={`${process.env.PUBLIC_URL}/images/misc/hero.png`}
                    alt="Login Illustration"
                    className="aspect-square w-4/5 rounded-lg bg-opacity-50"
                  />
                </div>
              </div>
            )}

            <div className={twMerge(hideSidePanel ? 'w-full' : 'w-full md:w-1/2')}>
              <div
                className={twMerge(
                  'rounded-lg p-2 pt-4 md:overflow-y-auto md:pt-2',
                  !hideSidePanel ? 'border border-primary-300 border-opacity-10 bg-secondary-50' : '',
                )}>
                {stepIndicator && <div className="flex w-full justify-center px-2 pt-2">{stepIndicator}</div>}

                {header && <div className="w-full">{header}</div>}
                <div className="flex w-full flex-col">
                  <div className="flex w-full flex-col px-2 py-1 pb-4 md:px-4 md:py-4">
                    {React.Children.toArray(children).filter(
                      (child) => React.isValidElement(child) && child.type !== Header && child.type !== SidePanel,
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

interface HeaderProps {
  children: React.ReactNode;
}

function Header({ children }: HeaderProps): JSX.Element {
  return <div className="flex h-fit w-full flex-col gap-1 px-2 md:gap-2 md:px-4">{children}</div>;
}

interface SidePanelProps {
  children: React.ReactNode;
}

function SidePanel({ children }: SidePanelProps): JSX.Element {
  return <>{children}</>;
}

ResponsePageWrapper.Header = Header;
ResponsePageWrapper.SidePanel = SidePanel;

export default ResponsePageWrapper;
